import {
  Component,
  computed,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavItem, NavLogoIcon } from './nav-menu.model';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { NavMenuService } from './nav-menu.service';
import { SvgIconComponent } from '@shared/components/svg-icon/svg-icon.component';

@Component({
  selector: '[nav-menu]',
  imports: [OverlayModule, SvgIconComponent],
  templateUrl: './nav-menu.html',
  styleUrl: './nav-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NavMenuService],
})
export class NavMenu {
  service = inject(NavMenuService);

  items = input.required<NavItem[]>();
  logoText = input.required<string>();
  logoIcon = input<NavLogoIcon | null>(null);
  logoIconType = computed(() => this.logoIcon()?.iconType);
  logoIconPath = computed(() => this.logoIcon()?.icon || '');

  isExpanded = signal(false);
  activeFlyout = signal<string | null>(null);
  openGroups = signal<Set<string>>(new Set());

  readonly flyoutPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
      offsetX: 8,
    },
  ];

  private closeTimer: any;

  toggleNav() {
    this.isExpanded.update((v) => !v);
  }

  handleNavigation(item: NavItem) {
    if (item.path) {
      this.service.navigateTo(item.path);
      this.activeFlyout.set(null);
    }
  }

  isGroup(item: NavItem): boolean {
    return item.type === 'group';
  }

  getPositions(item: NavItem): ConnectedPosition[] {
    if (this.isGroup(item)) {
      return this.flyoutPositions;
    }
    return [
      {
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
        offsetX: 8,
      },
    ];
  }

  toggleGroup(id: string) {
    if (!this.isExpanded()) return;

    this.openGroups.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }

      return newSet;
    });
  }

  isItemActive(item: NavItem) {
    return this.service.isActive(item);
  }

  showFlyout(id: string) {
    this.clearTimer();
    if (!this.isExpanded()) this.activeFlyout.set(id);
  }

  hideFlyout() {
    this.clearTimer();
    this.closeTimer = setTimeout(() => {
      this.activeFlyout.set(null);
    }, 150);
  }

  clearTimer() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
    }
  }
}
